import { createSlice, createAsyncThunk, type PayloadAction } from '@reduxjs/toolkit';
import {
  getSchemes,
  getSchemeById,
  createScheme as createSchemeApi,
  updateScheme as updateSchemeApi,
  removeSchemeApi,
  batchExtendSchemeValidity,
} from '../api/scheme';
import type { Scheme, SchemeFilters, CreateSchemeForm } from '../types/scheme';

interface SchemeState {
  schemes: Scheme[];
  selectedSchemeDetails: Scheme | null;
  selectedSchemeIds: string[];
  loading: boolean;
  error: string | null;
  successMessage: string | null;
  filters: SchemeFilters;
  totalEntries: number;
}

const initialState: SchemeState = {
  schemes: [],
  selectedSchemeDetails: null,
  selectedSchemeIds: [],
  loading: false,
  error: null,
  successMessage: null,
  filters: {
    search: '',
    location: 'All Locations',
    product: 'SKU Unit',
    status: 'SKU Unit',
    page: 1,
    limit: 25,
  },
  totalEntries: 135,
};

export const fetchSchemes = createAsyncThunk(
  'scheme/fetchSchemes',
  async (_, { getState, rejectWithValue }) => {
    try {
      const state = getState() as { scheme: SchemeState };
      const res = await getSchemes(state.scheme.filters);
      return res;
    } catch (err: any) {
      return rejectWithValue(err.message || 'Failed to fetch schemes');
    }
  }
);

export const fetchSchemeDetails = createAsyncThunk(
  'scheme/fetchSchemeDetails',
  async (id: string, { rejectWithValue }) => {
    try {
      const res = await getSchemeById(id);
      return res;
    } catch (err: any) {
      return rejectWithValue(err.message || 'Failed to fetch scheme details');
    }
  }
);

export const addScheme = createAsyncThunk(
  'scheme/addScheme',
  async (formData: CreateSchemeForm, { rejectWithValue }) => {
    try {
      const newScheme = await createSchemeApi(formData);
      return newScheme;
    } catch (err: any) {
      return rejectWithValue(err.message || 'Failed to create scheme');
    }
  }
);

export const editScheme = createAsyncThunk(
  'scheme/editScheme',
  async (formData: CreateSchemeForm, { rejectWithValue }) => {
    try {
      const updated = await updateSchemeApi(formData);
      return updated;
    } catch (err: any) {
      return rejectWithValue(err.message || 'Failed to update scheme');
    }
  }
);

export const deleteScheme = createAsyncThunk(
  'scheme/deleteScheme',
  async (id: string, { rejectWithValue }) => {
    try {
      await removeSchemeApi(id);
      return id;
    } catch (err: any) {
      return rejectWithValue(err.message || 'Failed to remove scheme');
    }
  }
);

export const extendValidity = createAsyncThunk(
  'scheme/extendValidity',
  async (days: 15 | 30, { getState, rejectWithValue }) => {
    try {
      const state = getState() as { scheme: SchemeState };
      const ids = state.scheme.selectedSchemeIds;
      if (ids.length === 0) {
        return rejectWithValue('Please select at least one scheme to extend validity.');
      }
      const res = await batchExtendSchemeValidity(ids, days);
      return res;
    } catch (err: any) {
      return rejectWithValue(err.message || 'Failed to extend validity');
    }
  }
);

const schemeSlice = createSlice({
  name: 'scheme',
  initialState,
  reducers: {
    setSearchFilter: (state, action: PayloadAction<string>) => {
      state.filters.search = action.payload;
      state.filters.page = 1;
    },
    setLocationFilter: (state, action: PayloadAction<string>) => {
      state.filters.location = action.payload;
      state.filters.page = 1;
    },
    setProductFilter: (state, action: PayloadAction<string>) => {
      state.filters.product = action.payload;
      state.filters.page = 1;
    },
    setStatusFilter: (state, action: PayloadAction<string>) => {
      state.filters.status = action.payload;
      state.filters.page = 1;
    },
    setPage: (state, action: PayloadAction<number>) => {
      state.filters.page = action.payload;
    },
    setLimit: (state, action: PayloadAction<number>) => {
      state.filters.limit = action.payload;
      state.filters.page = 1;
    },
    toggleSelectScheme: (state, action: PayloadAction<string>) => {
      const id = action.payload;
      if (state.selectedSchemeIds.includes(id)) {
        state.selectedSchemeIds = state.selectedSchemeIds.filter((item) => item !== id);
      } else {
        state.selectedSchemeIds.push(id);
      }
    },
    toggleSelectAllSchemes: (state) => {
      if (state.selectedSchemeIds.length === state.schemes.length && state.schemes.length > 0) {
        state.selectedSchemeIds = [];
      } else {
        state.selectedSchemeIds = state.schemes.map((s) => s.id);
      }
    },
    clearSelectedSchemes: (state) => {
      state.selectedSchemeIds = [];
    },
    clearSuccessMessage: (state) => {
      state.successMessage = null;
    },
    resetFilters: (state) => {
      state.filters = initialState.filters;
      state.selectedSchemeIds = [];
    },
  },
  extraReducers: (builder) => {
    builder
      // fetchSchemes
      .addCase(fetchSchemes.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchSchemes.fulfilled, (state, action) => {
        state.loading = false;
        state.schemes = action.payload.data;
        state.totalEntries = action.payload.total || 135;
      })
      .addCase(fetchSchemes.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // fetchSchemeDetails
      .addCase(fetchSchemeDetails.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchSchemeDetails.fulfilled, (state, action) => {
        state.loading = false;
        state.selectedSchemeDetails = action.payload;
      })
      .addCase(fetchSchemeDetails.rejected, (state) => {
        state.loading = false;
      })
      // addScheme
      .addCase(addScheme.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(addScheme.fulfilled, (state, action) => {
        state.loading = false;
        state.schemes.unshift(action.payload);
        state.totalEntries += 1;
        state.successMessage = `Scheme "${action.payload.name}" created successfully!`;
      })
      .addCase(addScheme.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // editScheme
      .addCase(editScheme.pending, (state) => {
        state.loading = true;
      })
      .addCase(editScheme.fulfilled, (state, action) => {
        state.loading = false;
        state.selectedSchemeDetails = action.payload;
        state.successMessage = `Scheme "${action.payload.name}" updated successfully!`;
      })
      .addCase(editScheme.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // deleteScheme
      .addCase(deleteScheme.fulfilled, (state, action) => {
        state.schemes = state.schemes.filter((s) => s.id !== action.payload);
        state.successMessage = `Scheme removed successfully.`;
      })
      // extendValidity
      .addCase(extendValidity.fulfilled, (state, action) => {
        const { count, days } = action.payload;
        state.successMessage = `Extended validity by ${days} days for ${count} selected scheme(s).`;
        state.selectedSchemeIds = [];
      });
  },
});

export const {
  setSearchFilter,
  setLocationFilter,
  setProductFilter,
  setStatusFilter,
  setPage,
  setLimit,
  toggleSelectScheme,
  toggleSelectAllSchemes,
  clearSelectedSchemes,
  clearSuccessMessage,
  resetFilters,
} = schemeSlice.actions;

export default schemeSlice.reducer;
